import { AxiosErrorType } from "@/lib/axios";
import { ValidationMessage } from "@/stores/validation";
import { z } from "zod";

export const REQUIRED = "必須です";
export const STR_INVALID = "文字を入力してください";
export const INT_INVALID = "整数を入力してください";
export const NUM_INVALID = "数値を入力してください";
export const AMOUNT_INVALID = "整数または小数点以下1桁までの値を入力してください";
export const MAX_STR_INVALID = "最大%d文字以内で入力してください";
export const MAX_NUM_INVALID = "最大%d桁で入力してください";
export const DATE_INVALID = "日付を入力してください";
export const EMAIL_INVALID = "メールの形式が正しくありません";
export const POST_CODE_LEN = "7桁で入力してください";
export const INVOICE_LEN = "13桁で入力してください";
export const HIRAGANA_INVALID = "ひらがなで入力してください";
export const HIRAGANA_SIGN_INVALID = "ひらがなと記号のみで入力してください";

const INT_REGEX = /^-?\d{1,3}(\d{3})*$/;
const AMOUNT_REGEX = /^-?\d+(?:\.\d{0,1})?$/;

const schema = {
  // NOTE: auth
  username: z.string().min(1, REQUIRED),
  password: z.string().min(1, REQUIRED),

  // NOTE: project(estimate, contract)
  project_name: z.string().min(1, REQUIRED).max(50, MAX_STR_INVALID.replace("%d", "50")),
  charge_id: z.string().min(1, REQUIRED),

  // NOTE:  estimate
  accrual_date: z.string().date(DATE_INVALID),
  accrual_place: z.string().min(1, REQUIRED).max(20, MAX_STR_INVALID.replace("%d", "20")),
  estimate_validity_period: z
    .string()
    .min(1, REQUIRED)
    .max(10, MAX_STR_INVALID.replace("%d", "10")),
  deadline: z.string().min(1, REQUIRED).max(10, MAX_STR_INVALID.replace("%d", "10")),
  delivery_place: z
    .string()
    .min(1, REQUIRED)
    .max(20, MAX_STR_INVALID.replace("%d", "20")),
  transaction_condition: z.string().max(20, MAX_STR_INVALID.replace("%d", "20")),

  // NOTE: project detail
  id: z.string().or(z.number()).nullish(),
  product_name: z.string().min(1, REQUIRED).max(50, MAX_STR_INVALID.replace("%d", "50")),
  model_name: z.union([z.literal(""), z.string().max(50, MAX_STR_INVALID.replace("%d", "50"))]).nullable(),
  amount_unit: z.string().or(z.literal("")).nullable(),
  amount: z.union([z.literal(""), z.null(), z.undefined(), z.number(), z.string().regex(AMOUNT_REGEX, AMOUNT_INVALID)]).optional(),
  unit_price: z.union([z.literal(""), z.null(), z.undefined(), z.number(), z.string().regex(INT_REGEX, INT_INVALID)]).optional(),
  remarks: z.union([z.literal(""), z.string().max(20, MAX_STR_INVALID.replace("%d", "20"))]).nullable(),

  // NOTE:  contract
  delivery_schedule_date: z.string().date(DATE_INVALID),
  invoice_schedule_date: z.string().date(DATE_INVALID),
  order_no: z.string().max(50, MAX_STR_INVALID.replace("%d", "50")).nullable(),

  // NOTE: client
  company_name: z.string().min(1, REQUIRED).max(50, MAX_STR_INVALID.replace("%d", "50")),
  company_kana: z
    .string()
    .min(1, REQUIRED)
    .max(50, MAX_STR_INVALID.replace("%d", "50"))
    .regex(
      /^[ぁ-ん！＃＄％＆（）*+，ー．＠［＼］＾＿｛｜｝〜!-/:-@¥[-`{-~]+$/,
      HIRAGANA_SIGN_INVALID
    ),
  post_code: z
    .string()
    .min(1, REQUIRED)
    .regex(/^\d{7}$/, POST_CODE_LEN),
  company_address: z
    .string()
    .min(1, REQUIRED)
    .max(250, MAX_STR_INVALID.replace("%d", "250")),
  invoice_code: z
    .string()
    .regex(/^\d{13}$/, INVOICE_LEN)
    .optional()
    .or(z.literal("")),
  bank_name: z.string().max(50, MAX_STR_INVALID.replace("%d", "50")),
  bank_code: z.string().max(7, MAX_NUM_INVALID.replace("%d", "7")),
  close_classification: z.string().min(1, REQUIRED),

  // NOTE: charge
  charge_name: z.string().min(1, REQUIRED).max(50, MAX_STR_INVALID.replace("%d", "50")),
  email: z
    .string()
    .max(100, MAX_STR_INVALID.replace("%d", "100"))
    .email({
      message: EMAIL_INVALID,
    })
    .regex(/^[\u0021-\u007e]+$/, EMAIL_INVALID)
    .optional()
    .or(z.literal("")),

  // NOTE: employee
  employee_name: z.string().min(1, REQUIRED).max(20, MAX_STR_INVALID.replace("%d", "20")),
  employee_kana: z
    .string()
    .min(1, REQUIRED)
    .max(20, MAX_STR_INVALID.replace("%d", "20"))
    .regex(/^[ぁ-ん]+$/, HIRAGANA_INVALID),
  position_code: z.string().min(1, REQUIRED),

  // NOTE: for download invoice
  payment_term: z.string().min(1, REQUIRED).max(20, MAX_STR_INVALID.replace("%d", "20")),

  // NOTE: for download delivery
  delivery_date: z
    .string({
      message: REQUIRED,
    })
    .min(1, REQUIRED)
    .date(DATE_INVALID),

  // NOTE: for download estimate
  estimate_date: z
    .string({
      message: REQUIRED,
    })
    .min(1, REQUIRED)
    .date(DATE_INVALID),

  issued_date: z
    .string({
      message: REQUIRED,
    })
    .min(1, REQUIRED)
    .date(DATE_INVALID),
};

const multi: {
  [key: string]: (keyof typeof schema)[];
} = {
  project_detail: [
    "id",
    "product_name",
    "model_name",
    "amount",
    "amount_unit",
    "unit_price",
    "remarks",
  ],
};

type ValidateRule = keyof typeof schema | keyof typeof multi;

export const getValidateRule = (keys: ValidateRule[]) => {
  let getSchema = {};
  keys.map((key: ValidateRule) => {
    if (Object.keys(multi).includes(String(key))) {
      getSchema = {
        ...getSchema,
        [key]: z.array(getValidateRule(multi[key])),
      };
    } else {
      if (Object.keys(schema).includes(String(key))) {
        getSchema = {
          ...getSchema,
          [key]: schema[key as keyof typeof schema],
        };
      }
    }
  });
  return z.object(getSchema);
};

export const resolveValidateResponse = (
  res: AxiosErrorType["result"]
): Omit<ValidationMessage, "id">[] => {
  if (!res) return [];

  const validates = Object.keys(res).map((key) => ({
    key: key,
    message: res[key].join("\n"),
  }));
  return validates;
};
